import React, { useEffect, useState } from "react";
import { useLoaderData, useNavigate } from "react-router-dom";
import {
  ArrowLeft2,
  ArrowRight2,
  BackSquare,
  SearchNormal,
  Sort,
} from "iconsax-react";
import { getToken } from "../../../utils/token";
import instance from "../../../axios/instance";
import { PaginationButton } from "../../../components/PaginationButton";
import { MedicationName } from "../../../components/MedicationName";
import { RxItemPage } from "../../../components/modals/order/RxOrderItem";
import CreateOrder from "../../../components/modals/order/CreateOrder";

export const orderRxsLoader = async ({ params }) => {
  const token = getToken();
  const rxs = await instance.post(
    "/doctor/orders/rx",
    { orderId: params.id },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  return {
    rxs: rxs.data,
    orderId: params.id,
  };
};

const OrderRxsPage = () => {
  let { rxs: _rxs, orderId } = useLoaderData();
  const [rxs, setRxs] = useState(
    [..._rxs].map((_, index) => ({ ..._, index: index + 1 })),
  );
  const [currentRx, setCurrentRx] = useState(null);
  const [filteredRxs, setFilteredRxs] = useState(
    rxs.filter((_, index) => index + 1 <= 10),
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [pageIndex, setPageIndex] = useState(1);
  const [sorted, setSorted] = useState("");
  const [selectedRx, setSelectedRx] = useState([]);
  const [isCreateOrderModalOpen, setIsCreateOrderModalOpen] = useState(false);
  const navigate = useNavigate();

  const handleSearch = (e) => {
    e.preventDefault();

    const query = searchQuery.toLowerCase();
    setFilteredRxs(() => {
      return rxs.filter((rx) => {
        const id = `${rx.id}`;
        const medicationName = rx.medication_name.toLowerCase().includes(query);

        return query ? id === query || medicationName : rx;
      });
    });
  };

  useEffect(() => {
    setFilteredRxs(() =>
      rxs.filter(
        (_, index) =>
          index + 1 > 10 * (pageIndex - 1) && index + 1 <= 10 * pageIndex,
      ),
    );
  }, [pageIndex]);

  const handleSort = (def) => {
    switch (def) {
      case "index": {
        setPageIndex(1);
        if (sorted !== "index") {
          setSorted("index");
        } else {
          setSorted("");
        }

        rxs.reverse();
        setRxs(rxs);
        setFilteredRxs((items) => rxs.filter((_, index) => index + 1 <= 10));
        return;
      }
      case "name": {
        setPageIndex(1);
        if (sorted === "name") {
          setSorted("");
          rxs.sort((a, b) => a.index - b.index);
        } else {
          setSorted("name");
          rxs.sort((a, b) => {
            if (a.medication_name < b.medication_name) return -1;
            if (a.medication_name > b.medication_name) return 1;
            return 0;
          });
        }
        setFilteredRxs((items) => rxs.filter((_, index) => index + 1 <= 10));
        return;
      }
      case "size": {
        setPageIndex(1);
        if (sorted === "size") {
          setSorted("");
          rxs.sort((a, b) => a.index - b.index);
        } else {
          setSorted("size");
          rxs.sort((a, b) => {
            if (a.size < b.size) return -1;
            if (a.size > b.size) return 1;
            return 0;
          });
        }
        setFilteredRxs((items) => rxs.filter((_, index) => index + 1 <= 10));
        return;
      }
      case "date": {
        setPageIndex(1);
        if (sorted === "date") {
          setSorted("");
          rxs.sort((a, b) => a.index - b.index);
        } else {
          setSorted("date");
          rxs.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        }
        setFilteredRxs((items) => rxs.filter((_, index) => index + 1 <= 10));
        return;
      }
    }
  };

  const toggleRowSelection = (id) => {
    if (selectedRx.includes(id)) {
      setSelectedRx(selectedRx.filter((rowId) => rowId !== id));
    } else {
      setSelectedRx([...selectedRx, id]);
    }
  };

  const toggleAllRows = () => {
    if (selectedRx.length === filteredRxs.length) {
      setSelectedRx([]);
    } else {
      setSelectedRx(filteredRxs.map((row) => row.id));
    }
  };

  return (
    <div className="px-7 py-5 bg-[#fafafa]">
      {isCreateOrderModalOpen && (
        <CreateOrder
          isOpen={isCreateOrderModalOpen}
          onClose={() => setIsCreateOrderModalOpen(false)}
          triggerRevalidate={() => (location.href = `/clinic/dashboard/orders`)}
          selectedRx={selectedRx}
        />
      )}
      <div className="flex justify-between items-center h-[50px]">
        <div className="flex gap-x-3 items-center">
          <BackSquare
            size={30}
            className="hover:text-primary duration-300 cursor-pointer"
            onClick={() => navigate(`/dashboard/orders`)}
          />
          <div className="font-bold text-2xl">Order RXs ({rxs.length})</div>
        </div>

        {selectedRx.length >= 1 && (
          <div className="flex gap-2 items-center">
            <button
              className="bg-[#00B0AD] py-2 px-3 shadow-sm rounded-[4px] font-medium text-[14px] text-white"
              onClick={() => setIsCreateOrderModalOpen(true)}
            >
              Create Refill Order +
            </button>
          </div>
        )}
      </div>

      <div className="border bg-white rounded-md overflow-x-scroll scrollbar-hide mt-4">
        <div className="flex justify-between p-2 items-center">
          <form className="relative" onSubmit={handleSearch}>
            <input
              className="outline-none h-[40px] w-[300px] border rounded-md p-2 placeholder placeholder:text-[14px] text-sm"
              placeholder="Search rx by ID and name"
              name="query"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button
              className="absolute h-[38px] rounded-md top-1/2 -translate-y-1/2 right-[1px] w-[30px] flex items-center justify-center bg-white cursor-pointer hover:text-primary duration-300"
              type="submit"
            >
              <SearchNormal size={15} />
            </button>
          </form>

          <div className="flex gap-2 items-center">
            <PaginationButton
              item={<ArrowLeft2 size={16} />}
              disabled={pageIndex <= 1}
              handleClick={() => setPageIndex(pageIndex - 1)}
            />
            <span className="font-light text-xs">Page {pageIndex}</span>
            <PaginationButton
              item={<ArrowRight2 size={16} />}
              disabled={pageIndex >= Math.ceil(rxs.length / 10)}
              handleClick={() => setPageIndex((index) => index + 1)}
            />
          </div>
        </div>
        <div className="max-h-[80vh] overflow-y-scroll scrollbar-hide">
          <table className="w-full relative whitespace-nowrap">
            <thead>
              <tr className="uppercase h-14 font-bold bg-[#f1f1f1] text-gray-700 text-[12px]">
                <td className="px-6 py-2 cursor-pointer hover:text-primary duration-300">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      className="w-4 h-4 rounded border-gray-300 accent-green-700"
                      checked={
                        selectedRx.length === filteredRxs.length &&
                        filteredRxs.length > 0
                      }
                      onChange={toggleAllRows}
                    />
                    <span onClick={() => handleSort("index")}>
                      #{" "}
                      <Sort
                        size="15"
                        className={`${
                          sorted === "index" ? "rotate-180" : "rotate-0"
                        } inline`}
                        color="#FF8A65"
                      />
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4">RX ID</td>
                <td
                  className="px-6 py-4 cursor-pointer hover:text-primary duration-300"
                  onClick={() => handleSort("name")}
                >
                  Medication Name{" "}
                  <Sort
                    size="15"
                    className={`${
                      sorted === "name" ? "rotate-180" : "rotate-0"
                    } inline`}
                    color="#FF8A65"
                  />
                </td>
                <td className="px-6 py-4">Fill ID</td>
                <td
                  className="px-6 py-4 cursor-pointer hover:text-primary duration-300"
                  onClick={() => handleSort("size")}
                >
                  Size{" "}
                  <Sort
                    size="15"
                    className={`${
                      sorted === "size" ? "rotate-180" : "rotate-0"
                    } inline`}
                    color="#FF8A65"
                  />
                </td>
                <td
                  className="px-6 py-4 cursor-pointer hover:text-primary duration-300"
                  onClick={() => handleSort("date")}
                >
                  Date{" "}
                  <Sort
                    size="15"
                    className={`${
                      sorted === "date" ? "rotate-180" : "rotate-0"
                    } inline`}
                    color="#FF8A65"
                  />
                </td>
                <td></td>
              </tr>
            </thead>

            <tbody>
              {rxs.length < 1 ? (
                <tr>
                  <td className="px-6 py-4" colSpan={6}>
                    No dispensed rx for this order
                  </td>
                </tr>
              ) : (
                filteredRxs.map((rx, index) => (
                  <tr key={rx.index} className="h-14 text-[14px] border-b">
                    <td className="px-6 py-2">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          className="w-4 h-4 border-gray-300 !accent-green-700 rounded-md"
                          checked={selectedRx.includes(rx.id)}
                          onChange={() => toggleRowSelection(rx.id)}
                        />
                        {rx.index}
                      </div>
                    </td>
                    <td className="px-6 py-4">{rx.id}</td>
                    <td className="px-6 py-4">
                      <MedicationName
                        med={{
                          name: rx.medication_name,
                          c2: rx.c2,
                        }}
                      />
                    </td>
                    <td className="px-6 py-4 capitalize">{rx.fill_id}</td>
                    <td className="px-6 py-4 capitalize">{rx.size}</td>
                    <td className="px-6 py-4">
                      {new Date(rx.created_at).toLocaleString().split(",")[0]}
                    </td>
                    <td
                      onClick={() => setCurrentRx(rx)}
                      className="px-6 py-4 underline text-primary cursor-pointer"
                    >
                      View RX
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      {currentRx && (
        <RxItemPage rx={currentRx} onClose={() => setCurrentRx(null)} />
      )}
    </div>
  );
};

export default OrderRxsPage;
